import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { compressImage } from "../lib/imageOptimizer";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { BarChart3, Users, KanbanSquare, GraduationCap, Upload, Check, CheckCircle2, Trash2, X, Plus, BookOpen } from "lucide-react";
import { toast } from "sonner";

const FILES_ORIGIN = API.replace(/\/api\/?$/, "");
const abs = (u) => (!u ? "" : /^https?:\/\//.test(u) ? u : `${FILES_ORIGIN}${u}`);
const HEX = /^#[0-9a-fA-F]{6}$/;
const subscriptionDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const CSV_HEADER_WORDS = new Set(["course", "program", "programme", "name", "degree"]);
const MAX_COURSES_CLIENT = 50; // mirrors routes_institution.py's MAX_COURSES

// Deliberately not a full RFC4180 parser — a plain split is what's asked
// for ("without external heavy libraries... split lines by \n or commas"),
// and course-name lists don't realistically contain embedded commas. Only
// the first column is read, so "MBA,Marketing,Finance" style rows (course
// + specializations) still yield just the course name.
function parseCourseCsv(text) {
  const lines = text.split(/\r?\n/);
  if (!lines.length) return [];
  const firstCell = (lines[0].split(",")[0] || "").trim().replace(/^"|"$/g, "").toLowerCase();
  const body = CSV_HEADER_WORDS.has(firstCell) ? lines.slice(1) : lines;
  const out = [];
  for (const line of body) {
    const cell = (line.split(",")[0] || "").trim().replace(/^"|"$/g, "").trim();
    if (cell) out.push(cell);
  }
  return out;
}

export default function InstitutionSettings() {
  const { refreshInstitution } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef();
  const faviconRef = useRef();
  const heroRef = useRef();
  const courseCsvRef = useRef();

  const [courses, setCourses] = useState(null);
  const [newCourse, setNewCourse] = useState("");
  const [coursesBusy, setCoursesBusy] = useState(false);
  const [coursesDirty, setCoursesDirty] = useState(false);

  const loadCourses = () => api.get("/admin/institution/courses")
    .then((r) => { setCourses(r.data.courses || []); setCoursesDirty(false); })
    .catch(() => toast.error("Could not load the course catalog"));

  useEffect(() => { loadCourses(); }, []);

  const load = () => api.get("/admin/institution/branding").then((r) => {
    setData(r.data);
    const lh = r.data.branding.login_hero || {};
    setForm({
      name: r.data.name || "",
      primary_color: r.data.branding.primary_color || "#1E40AF",
      accent_color: r.data.branding.accent_color || "#3B82F6",
      support_email: r.data.branding.support_email || "",
      support_phone: r.data.branding.support_phone || "",
      header_title: r.data.portal_config.header_title || "",
      base_application_fee: r.data.portal_config.base_application_fee ?? 1500,
      hero_brand_name: lh.brand_name || "",
      hero_headline: lh.headline || "",
      hero_bullets: (lh.bullets || []).join("\n"),
      hero_logo_url: lh.logo_url || "",
    });
  }).catch(() => toast.error("Could not load branding"));

  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!HEX.test(form.primary_color) || !HEX.test(form.accent_color)) {
      toast.error("Colours must be #rrggbb hex");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/institution/branding", {
        name: form.name,
        primary_color: form.primary_color,
        accent_color: form.accent_color,
        support_email: form.support_email,
        support_phone: form.support_phone,
        header_title: form.header_title,
        base_application_fee: Number(form.base_application_fee) || 0,
        login_hero: {
          brand_name: form.hero_brand_name.trim(),
          headline: form.hero_headline,
          bullets: form.hero_bullets.split("\n").map((s) => s.trim()).filter(Boolean),
          logo_url: form.hero_logo_url.trim(),
        },
      });
      await load();
      await refreshInstitution();
      toast.success("Branding saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    // Shrink an oversized logo bitmap; keep PNG (alpha) as PNG. SVG passes
    // through untouched. Favicons are tiny by definition — leave them alone.
    const prepared = kind === "logo"
      ? await compressImage(file, {
          maxWidthOrHeight: 512,
          maxSizeMB: 0.3,
          fileType: file.type === "image/png" ? "image/png" : "image/jpeg",
        })
      : file;
    const fd = new FormData();
    fd.append("file", prepared);
    try {
      await api.post(`/admin/institution/branding/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      await refreshInstitution();
      toast.success(`${kind === "logo" ? "Logo" : "Favicon"} updated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const prepared = await compressImage(file, { maxWidthOrHeight: 2000, maxSizeMB: 1.2, fileType: "image/jpeg" });
    const fd = new FormData();
    fd.append("file", prepared);
    try {
      await api.post("/admin/institution/branding/login-hero", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Login background updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const clearHero = async () => {
    try {
      await api.delete("/admin/institution/branding/login-hero");
      await load();
      toast.success("Login background cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addCourse = () => {
    const name = newCourse.trim();
    if (!name) return;
    if (courses.some((c) => c.toLowerCase() === name.toLowerCase())) {
      toast.error(`"${name}" is already in the catalog`);
      return;
    }
    setCourses((c) => [...c, name]);
    setCoursesDirty(true);
    setNewCourse("");
  };

  const removeCourse = (name) => {
    setCourses((c) => c.filter((x) => x !== name));
    setCoursesDirty(true);
  };

  const importCoursesCsv = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCourseCsv(String(reader.result || ""));
      // Case-insensitive dedupe against what's already a chip, and against
      // duplicates within the file itself — the course name's own casing
      // (however the admin's spreadsheet wrote it) is kept as-is rather than
      // force-normalized, since these are often meaningful acronyms
      // (BBA, MBA, MBBS) a blind title-case/lowercase pass would mangle.
      const seen = new Set(courses.map((c) => c.toLowerCase()));
      const added = [];
      for (const name of parsed) {
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        added.push(name.slice(0, 80));
      }
      if (!added.length) {
        toast.error("No new courses found in that file.");
        return;
      }
      setCourses((c) => {
        const merged = [...c, ...added];
        if (merged.length > MAX_COURSES_CLIENT) {
          toast.error(`Only the first ${MAX_COURSES_CLIENT} courses can be saved — trim the list before saving.`);
        }
        return merged;
      });
      setCoursesDirty(true);
      toast.success(`Imported ${added.length} course${added.length === 1 ? "" : "s"}. Click "Save courses" to apply.`);
    };
    reader.onerror = () => toast.error("Could not read that file.");
    reader.readAsText(file);
  };

  const saveCourses = async () => {
    if (!courses.length) {
      toast.error("At least one course is required");
      return;
    }
    setCoursesBusy(true);
    try {
      const { data: res } = await api.put("/admin/institution/courses", { courses });
      setCourses(res.courses);
      setCoursesDirty(false);
      toast.success("Course catalog saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCoursesBusy(false); }
  };

  if (!form) {
    return <div className="py-32 flex justify-center"><div className="w-7 h-7 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>;
  }

  const logo = abs(data?.branding?.logo_url);
  const primary = HEX.test(form.primary_color) ? form.primary_color : "#1E40AF";
  const accent = HEX.test(form.accent_color) ? form.accent_color : "#3B82F6";

  const DEFAULT_HERO_BG =
    "https://images.unsplash.com/photo-1583373834259-46cc92173cb7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
  const DEFAULT_HERO_BULLETS = [
    "Real-time publisher funnel analytics across 12+ states",
    "Deduplicated multi-instance lead engine with tiered attribution",
    "Quota-enforced webhooks with token-signed ingestion",
  ];
  const heroBgStored = data?.branding?.login_hero?.background_image_url || "";
  const heroBg = abs(heroBgStored) || DEFAULT_HERO_BG;
  const heroLogo = abs(form.hero_logo_url || data?.branding?.logo_url);
  const heroBrand = form.hero_brand_name || form.name || "EduPulse CRM";
  const heroLines = (form.hero_headline || "Every inquiry.\nEvery attribution.\nOne admissions engine.").split("\n");
  const heroBulletsArr = form.hero_bullets.split("\n").map((s) => s.trim()).filter(Boolean);
  const heroBullets = heroBulletsArr.length ? heroBulletsArr : DEFAULT_HERO_BULLETS;

  return (
    <div data-testid="institution-settings-page" className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Institution &amp; Branding</h1>
          <p className="text-sm text-slate-500 mt-1">
            White-label the CRM and the public application portal. <span className="font-mono text-xs">{data?.id}</span>
          </p>
        </div>
        {/* Read-only — a tenant's own super_admin/dean can see their plan
            and expiry for awareness, but has no edit/renew/financial
            capability at all; that's routes_platform.py's
            require_platform_owner-gated SaaS Control Panel only. */}
        {data?.subscription && (
          <Badge variant="secondary" className="text-xs font-normal py-1.5 px-3" data-testid="subscription-readonly-badge">
            Plan: <span className="font-medium capitalize mx-1">{data.subscription.plan_tier}</span>
            · Valid until: <span className="font-medium ml-1">{subscriptionDate(data.subscription.valid_until)}</span>
          </Badge>
        )}
      </div>

      {/* live preview */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="brand-preview">
        <Card className="p-0 overflow-hidden border-slate-200">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">CRM sidebar</div>
          <div className="flex">
            <div className="w-44 bg-white border-r border-slate-200">
              <div className="h-14 flex items-center gap-2 px-3 border-b border-slate-100">
                {logo ? <img src={logo} alt="" className="h-7 w-7 rounded object-contain" />
                      : <span className="h-7 w-7 rounded grid place-items-center" style={{ background: primary }}><GraduationCap className="w-4 h-4 text-white" /></span>}
                <span className="text-xs font-bold text-slate-800 truncate">{form.name || "Institution"}</span>
              </div>
              {[["Analytics", BarChart3, true], ["Leads", Users, false], ["Pipeline", KanbanSquare, false]].map(([l, Ic, act]) => (
                <div key={l} className="flex items-center gap-2 px-3 py-2 text-xs font-medium"
                  style={act ? { background: primary, color: "#fff" } : { color: "#475569" }}>
                  <Ic className="w-3.5 h-3.5" /> {l}
                </div>
              ))}
            </div>
            <div className="flex-1 p-4 bg-slate-50">
              <div className="h-8 rounded-md text-white text-xs font-semibold inline-flex items-center px-3" style={{ background: primary }}>
                Primary action
              </div>
              <div className="mt-3 h-1.5 w-2/3 rounded-full" style={{ background: accent }} />
            </div>
          </div>
        </Card>

        <Card className="p-0 overflow-hidden border-slate-200">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">Public /apply banner</div>
          <div className="bg-white">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
              {logo ? <img src={logo} alt="" className="h-8 w-8 rounded object-contain" />
                    : <span className="h-8 w-8 rounded grid place-items-center" style={{ background: primary }}><GraduationCap className="w-4 h-4 text-white" /></span>}
              <div>
                <div className="text-sm font-bold text-slate-800">{form.name || "Institution"}</div>
                <div className="text-[11px] text-slate-500">{form.header_title || "Online Application"}</div>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4].map((n) => (
                  <span key={n} className="w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold text-white"
                    style={{ background: n === 1 ? primary : "#cbd5e1" }}>{n}</span>
                ))}
              </div>
              <div className="mt-3 h-8 rounded-md text-white text-xs font-semibold inline-flex items-center px-3" style={{ background: primary }}>
                Continue
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* colours */}
      <Card className="p-5 border-slate-200 space-y-4">
        <h2 className="font-semibold text-slate-800">Brand colours</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["primary_color", "Primary colour"], ["accent_color", "Accent colour"]].map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs">{label}</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={HEX.test(form[k]) ? form[k] : "#1E40AF"}
                  data-testid={`color-${k}`} onChange={(e) => set(k)(e.target.value)}
                  className="h-9 w-12 rounded border border-slate-200 bg-white p-0.5" />
                <Input value={form[k]} onChange={(e) => set(k)(e.target.value)} placeholder="#1E40AF"
                  className={`h-9 w-36 font-mono text-sm ${HEX.test(form[k]) ? "" : "border-red-400"}`} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* assets */}
      <Card className="p-5 border-slate-200 space-y-4">
        <h2 className="font-semibold text-slate-800">Logo &amp; favicon</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Logo (PNG / JPEG / WebP, ≤ 2 MB)</Label>
            <div className="mt-2 flex items-center gap-3">
              <span className="h-14 w-14 rounded-lg border border-slate-200 grid place-items-center bg-slate-50 overflow-hidden">
                {logo ? <img src={logo} alt="logo" className="max-h-full max-w-full object-contain" />
                      : <GraduationCap className="w-6 h-6 text-slate-300" />}
              </span>
              <Button variant="outline" size="sm" data-testid="upload-logo" onClick={() => logoRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1.5" /> Upload logo
              </Button>
              {/* SVG deliberately excluded — an uploaded logo is served
                  publicly, unsanitized, straight off /uploads with no CSP
                  restricting script execution, and SVG is XML that can
                  carry a <script>/onload payload (stored XSS). The backend
                  (storage.BRANDING_LOGO_TYPES) enforces this independently
                  of what's offered here. */}
              <input ref={logoRef} type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                onChange={(e) => upload("logo", e.target.files?.[0])} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Favicon (.ico / .png)</Label>
            <div className="mt-2 flex items-center gap-3">
              <span className="h-14 w-14 rounded-lg border border-slate-200 grid place-items-center bg-slate-50 overflow-hidden">
                {data?.branding?.favicon_url ? <img src={abs(data.branding.favicon_url)} alt="favicon" className="h-8 w-8 object-contain" />
                  : <span className="text-slate-300 text-xs">none</span>}
              </span>
              <Button variant="outline" size="sm" data-testid="upload-favicon" onClick={() => faviconRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1.5" /> Upload favicon
              </Button>
              <input ref={faviconRef} type="file" className="hidden" accept=".ico,.png,image/x-icon,image/vnd.microsoft.icon,image/png"
                onChange={(e) => upload("favicon", e.target.files?.[0])} />
            </div>
          </div>
        </div>
      </Card>

      {/* login page hero */}
      <Card className="p-5 border-slate-200 space-y-4" data-testid="login-hero-settings">
        <div>
          <h2 className="font-semibold text-slate-800">Login page hero</h2>
          <p className="text-xs text-slate-500 mt-0.5">The promotional panel on the left of the sign-in screen.</p>
        </div>

        {/* live preview */}
        <div className="rounded-lg overflow-hidden border border-slate-200" data-testid="login-hero-preview">
          <div className="relative bg-slate-900 text-white p-5 min-h-[200px] flex flex-col justify-between gap-4"
            style={{ backgroundImage: `url(${heroBg})`, backgroundSize: "cover", backgroundPosition: "center" }}>
            <div className="absolute inset-0 bg-slate-900/75" />
            <div className="relative flex items-center gap-2">
              {heroLogo
                ? <img src={heroLogo} alt="" className="w-8 h-8 rounded object-contain bg-white/10 p-0.5" />
                : <span className="w-8 h-8 rounded bg-white/10 grid place-items-center"><GraduationCap className="w-4 h-4" /></span>}
              <span className="font-display text-sm font-bold">{heroBrand}</span>
            </div>
            <div className="relative">
              <div className="font-display text-lg font-bold leading-tight">
                {heroLines.map((l, i) => <div key={i}>{l || <br />}</div>)}
              </div>
              <div className="mt-3 space-y-1.5">
                {heroBullets.slice(0, 4).map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-px" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Brand name</Label>
            <Input className="mt-1" data-testid="hero-brand-name" value={form.hero_brand_name}
              placeholder={form.name || "EduPulse CRM"}
              onChange={(e) => set("hero_brand_name")(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Hero logo URL (optional)</Label>
            <Input className="mt-1 font-mono text-xs" data-testid="hero-logo-url" value={form.hero_logo_url}
              placeholder="leave blank to use the main logo"
              onChange={(e) => set("hero_logo_url")(e.target.value)} />
          </div>
        </div>

        <div>
          <Label className="text-xs">Headline (one line per row)</Label>
          <Textarea className="mt-1 font-display" rows={3} data-testid="hero-headline" value={form.hero_headline}
            placeholder={"Every inquiry.\nEvery attribution.\nOne admissions engine."}
            onChange={(e) => set("hero_headline")(e.target.value)} />
        </div>

        <div>
          <Label className="text-xs">Bullet points (one per line, up to 6)</Label>
          <Textarea className="mt-1" rows={4} data-testid="hero-bullets" value={form.hero_bullets}
            placeholder={"Real-time publisher funnel analytics\nDeduplicated multi-instance lead engine\nQuota-enforced webhooks"}
            onChange={(e) => set("hero_bullets")(e.target.value)} />
        </div>

        <div>
          <Label className="text-xs">Background image (PNG / JPEG / WebP, ≤ 6 MB)</Label>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="h-14 w-24 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center">
              {heroBgStored
                ? <img src={abs(heroBgStored)} alt="hero" className="h-full w-full object-cover" />
                : <span className="text-[10px] text-slate-400 px-1 text-center">default photo</span>}
            </span>
            <Button variant="outline" size="sm" data-testid="upload-hero" onClick={() => heroRef.current?.click()}>
              <Upload className="w-4 h-4 mr-1.5" /> Upload background
            </Button>
            {heroBgStored && (
              <Button variant="ghost" size="sm" data-testid="clear-hero" onClick={clearHero} className="text-slate-500">
                <Trash2 className="w-4 h-4 mr-1.5" /> Clear
              </Button>
            )}
            <input ref={heroRef} type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
              onChange={(e) => uploadHero(e.target.files?.[0])} />
          </div>
        </div>
      </Card>

      {/* profile + portal config */}
      <Card className="p-5 border-slate-200 space-y-4">
        <h2 className="font-semibold text-slate-800">Profile &amp; portal</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Institution name</Label>
            <Input className="mt-1" value={form.name} data-testid="brand-name" onChange={(e) => set("name")(e.target.value)} /></div>
          <div><Label className="text-xs">Portal welcome title</Label>
            <Input className="mt-1" value={form.header_title} data-testid="brand-header-title"
              placeholder="Aurora Institute of Technology — Online Application"
              onChange={(e) => set("header_title")(e.target.value)} /></div>
          <div><Label className="text-xs">Support email</Label>
            <Input className="mt-1" type="email" value={form.support_email} data-testid="brand-support-email"
              onChange={(e) => set("support_email")(e.target.value)} /></div>
          <div><Label className="text-xs">Support phone</Label>
            <Input className="mt-1" value={form.support_phone} data-testid="brand-support-phone"
              onChange={(e) => set("support_phone")(e.target.value)} /></div>
          <div><Label className="text-xs">Base application fee (₹, before GST)</Label>
            <Input className="mt-1 w-40" type="number" min={0} value={form.base_application_fee} data-testid="brand-fee"
              onChange={(e) => set("base_application_fee")(e.target.value)} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <span className="text-xs text-slate-500">Domains:</span>
          {(data?.domains || []).map((d) => <Badge key={d} variant="secondary" className="font-mono">{d}</Badge>)}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button className="bg-brand" data-testid="brand-save" disabled={busy} onClick={save}>
          <Check className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save branding"}
        </Button>
      </div>

      {/* programs & courses catalog */}
      <Card className="p-5 border-slate-200 space-y-4" data-testid="courses-settings">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-slate-400 shrink-0" />
            <div>
              <h2 className="font-semibold text-slate-800">Programs &amp; Courses</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                The course list offered on the public application form, analytics filters, and the Walk-in Lead form.
              </p>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" className="shrink-0" data-testid="course-import-csv"
            onClick={() => courseCsvRef.current?.click()} disabled={courses === null}>
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
          </Button>
          <input
            ref={courseCsvRef} type="file" accept=".csv,text/csv" className="hidden"
            data-testid="course-csv-input"
            onChange={(e) => { importCoursesCsv(e.target.files?.[0]); e.target.value = ""; }}
          />
        </div>

        {courses === null ? (
          <div className="py-6 flex justify-center"><div className="w-5 h-5 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="course-chip-list">
              {courses.length === 0 && <p className="text-sm text-slate-400">No courses yet — add one below.</p>}
              {courses.map((c) => (
                <span key={c} data-testid={`course-chip-${c}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 pl-3 pr-1.5 py-1 text-sm">
                  {c}
                  <button type="button" onClick={() => removeCourse(c)} aria-label={`Remove ${c}`}
                    data-testid={`course-remove-${c}`}
                    className="w-5 h-5 rounded-full grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Input
                value={newCourse} data-testid="course-new-input"
                onChange={(e) => setNewCourse(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCourse(); } }}
                placeholder="e.g. B.Tech Computer Science" className="h-9 max-w-xs"
              />
              <Button type="button" variant="outline" size="sm" data-testid="course-add" onClick={addCourse} disabled={!newCourse.trim()}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            <div className="flex justify-end">
              <Button data-testid="course-save" disabled={coursesBusy || !coursesDirty} onClick={saveCourses}>
                <Check className="w-4 h-4 mr-1.5" /> {coursesBusy ? "Saving…" : "Save courses"}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
